import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Save, Sparkles, Loader2, Home, Building2,
  MapPin, DollarSign, BedDouble, Bath, Car, Maximize,
  Image, Video, Eye, Trash2, Upload, Wand2, CheckCircle,
  X, ZoomIn, GripVertical, Star
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PROPERTY_TYPES = [
  { id: 'casa', name: 'Casa', icon: Home },
  { id: 'departamento', name: 'Departamento', icon: Building2 },
  { id: 'terreno', name: 'Terreno', icon: MapPin },
  { id: 'local_comercial', name: 'Local Comercial', icon: Building2 },
  { id: 'oficina', name: 'Oficina', icon: Building2 },
  { id: 'bodega', name: 'Bodega', icon: Building2 },
];

const OPERATION_TYPES = [
  { id: 'venta', name: 'Venta' },
  { id: 'renta', name: 'Renta' },
  { id: 'venta_renta', name: 'Venta o Renta' },
];

const AMENITIES_OPTIONS = [
  'Alberca', 'Gimnasio', 'Seguridad 24/7', 'Estacionamiento techado',
  'Jardín', 'Roof garden', 'Área de juegos', 'Salón de eventos',
  'Elevador', 'Portero', 'Pet friendly', 'Cuarto de servicio',
  'Bodega', 'Balcón', 'Terraza', 'Vista panorámica'
];

const DESCRIPTION_TONES = [
  { id: 'profesional', name: 'Profesional', desc: 'Formal, para inversores' },
  { id: 'casual', name: 'Casual', desc: 'Amigable, para familias' },
  { id: 'premium', name: 'Premium', desc: 'Exclusivo, de lujo' },
];

export default function PropertyFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  const token = localStorage.getItem('upim_token');
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [selectedTone, setSelectedTone] = useState('profesional');
  const [showToneSelector, setShowToneSelector] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    property_type: 'casa',
    operation_type: 'venta',
    address: '',
    colony: '',
    city: '',
    state: '',
    zip_code: '',
    price: '',
    bedrooms: 0,
    bathrooms: 0,
    half_bathrooms: 0,
    parking_spaces: 0,
    construction_area: '',
    land_area: '',
    levels: 1,
    age_years: '',
    amenities: [],
    images: [],
  });
  
  const [uploadingImages, setUploadingImages] = useState(false);
  const [imageError, setImageError] = useState('');
  const [previewImage, setPreviewImage] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const dragOverIndex = useRef(null);

  // Handle image upload
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    if (files.length > 20) {
      setImageError('Máximo 20 imágenes a la vez');
      return;
    }
    
    setUploadingImages(true);
    setImageError('');
    
    try {
      const formDataUpload = new FormData();
      files.forEach(file => {
        formDataUpload.append('files', file);
      });
      if (id) {
        formDataUpload.append('property_id', id);
      }
      
      const res = await axios.post(
        `${API_URL}/api/crm/properties/upload-images`,
        formDataUpload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (res.data.uploaded) {
        const newImages = res.data.uploaded.map(img => ({
          url: img.url,
          filename: img.filename
        }));
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, ...newImages]
        }));
      }
      
      if (res.data.errors?.length) {
        setImageError(res.data.errors.join(', '));
      }
    } catch (err) {
      console.error('Error uploading images:', err);
      setImageError('Error al subir imágenes');
    } finally {
      setUploadingImages(false);
    }
  };

  // Remove image
  const removeImage = async (index, filename) => {
    try {
      await axios.delete(
        `${API_URL}/api/crm/properties/delete-image/${filename}${id ? `?property_id=${id}` : ''}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setFormData(prev => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      }));
    } catch (err) {
      console.error('Error deleting image:', err);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    dragOverIndex.current = index;
  };

  const handleDragEnd = () => {
    if (draggedIndex !== null && dragOverIndex.current !== null && draggedIndex !== dragOverIndex.current) {
      const newImages = [...formData.images];
      const draggedItem = newImages[draggedIndex];
      newImages.splice(draggedIndex, 1);
      newImages.splice(dragOverIndex.current, 0, draggedItem);
      setFormData(prev => ({ ...prev, images: newImages }));
    }
    setDraggedIndex(null);
    dragOverIndex.current = null;
  };

  // Set image as main (move to first position)
  const setAsMainImage = (index) => {
    if (index === 0) return;
    const newImages = [...formData.images];
    const [selected] = newImages.splice(index, 1);
    newImages.unshift(selected);
    setFormData(prev => ({ ...prev, images: newImages }));
  };

  useEffect(() => {
    if (isEditing) {
      loadProperty();
    }
  }, [id]);

  const loadProperty = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/crm/properties/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData({
        ...res.data,
        price: res.data.price || '',
        construction_area: res.data.construction_area || '',
        land_area: res.data.land_area || '',
        age_years: res.data.age_years || '',
      });
    } catch (err) {
      console.error('Error loading property:', err);
      alert('Error al cargar la propiedad');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleAmenity = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const generateAIDescription = async () => {
    setGeneratingAI(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/crm/properties/ai/generate-description`,
        {
          property_id: id || null,
          property_type: formData.property_type,
          operation_type: formData.operation_type,
          bedrooms: formData.bedrooms,
          bathrooms: formData.bathrooms,
          construction_area: formData.construction_area ? parseFloat(formData.construction_area) : null,
          land_area: formData.land_area ? parseFloat(formData.land_area) : null,
          city: formData.city,
          colony: formData.colony,
          price: formData.price ? parseFloat(formData.price) : null,
          amenities: formData.amenities,
          tone: selectedTone
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.success) {
        setFormData(prev => ({ ...prev, description: res.data.description }));
      }
    } catch (err) {
      console.error('Error generating description:', err);
      alert('Error al generar la descripción');
    } finally {
      setGeneratingAI(false);
      setShowToneSelector(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const payload = {
        ...formData,
        price: formData.price ? parseFloat(formData.price) : null,
        construction_area: formData.construction_area ? parseFloat(formData.construction_area) : null,
        land_area: formData.land_area ? parseFloat(formData.land_area) : null,
        age_years: formData.age_years ? parseInt(formData.age_years) : null,
      };
      
      if (isEditing) {
        await axios.put(`${API_URL}/api/crm/properties/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        const res = await axios.post(`${API_URL}/api/crm/properties/create`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        navigate(`/crm/propiedades/${res.data.property_id}`);
        return;
      }
      
      alert('Propiedad guardada exitosamente');
    } catch (err) {
      console.error('Error saving property:', err);
      alert(err.response?.data?.detail || 'Error al guardar la propiedad');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!formData.title || !formData.price) {
      alert('Necesitas al menos título y precio para publicar');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/crm/properties/${id}/publish`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('¡Propiedad publicada exitosamente!');
      loadProperty();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al publicar');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/crm/propiedades')}
            className="p-2 hover:bg-gray-200 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditing ? 'Editar Propiedad' : 'Nueva Propiedad'}
            </h1>
            <p className="text-gray-500">Completa la información de tu propiedad</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold mb-4">Información Básica</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Título de la propiedad *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ej: Casa en venta en Polanco"
                  required
                />
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de propiedad
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {PROPERTY_TYPES.map(type => {
                      const Icon = type.icon;
                      return (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => handleChange('property_type', type.id)}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            formData.property_type === type.id
                              ? 'border-indigo-500 bg-indigo-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <Icon className="w-5 h-5 mx-auto mb-1" />
                          <span className="text-xs">{type.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de operación
                  </label>
                  <div className="flex gap-2">
                    {OPERATION_TYPES.map(op => (
                      <button
                        key={op.id}
                        type="button"
                        onClick={() => handleChange('operation_type', op.id)}
                        className={`flex-1 py-3 rounded-lg border-2 transition-all ${
                          formData.operation_type === op.id
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {op.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Description with AI */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Descripción
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowToneSelector(!showToneSelector)}
                      disabled={generatingAI}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg hover:from-indigo-600 hover:to-purple-600 disabled:opacity-50"
                    >
                      {generatingAI ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Wand2 className="w-4 h-4" />
                      )}
                      Generar con IA
                    </button>
                    
                    {showToneSelector && (
                      <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-lg border z-10 p-3">
                        <p className="text-sm font-medium text-gray-700 mb-2">Selecciona el tono:</p>
                        <div className="space-y-2">
                          {DESCRIPTION_TONES.map(tone => (
                            <button
                              key={tone.id}
                              type="button"
                              onClick={() => {
                                setSelectedTone(tone.id);
                                generateAIDescription();
                              }}
                              className={`w-full text-left p-2 rounded-lg transition-colors ${
                                selectedTone === tone.id ? 'bg-indigo-100' : 'hover:bg-gray-100'
                              }`}
                            >
                              <p className="font-medium text-sm">{tone.name}</p>
                              <p className="text-xs text-gray-500">{tone.desc}</p>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  rows={6}
                  placeholder="Describe tu propiedad o usa IA para generar una descripción profesional..."
                />
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-indigo-600" />
              Ubicación
            </h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Colonia</label>
                <input
                  type="text"
                  value={formData.colony}
                  onChange={(e) => handleChange('colony', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ej: Polanco"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ciudad</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ej: Ciudad de México"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ej: CDMX"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Código Postal</label>
                <input
                  type="text"
                  value={formData.zip_code}
                  onChange={(e) => handleChange('zip_code', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ej: 11560"
                />
              </div>
            </div>
          </div>

          {/* Price */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Precio
            </h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Precio (MXN) *</label>
                <input
                  type="number"
                  value={formData.price}
                  onChange={(e) => handleChange('price', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ej: 5000000"
                />
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold mb-4">Características</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <BedDouble className="w-4 h-4 inline mr-1" />
                  Recámaras
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.bedrooms}
                  onChange={(e) => handleChange('bedrooms', parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Bath className="w-4 h-4 inline mr-1" />
                  Baños
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={formData.bathrooms}
                  onChange={(e) => handleChange('bathrooms', parseFloat(e.target.value) || 0)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Car className="w-4 h-4 inline mr-1" />
                  Estacionamientos
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.parking_spaces}
                  onChange={(e) => handleChange('parking_spaces', parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Maximize className="w-4 h-4 inline mr-1" />
                  m² Construidos
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.construction_area}
                  onChange={(e) => handleChange('construction_area', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>

          {/* Amenities */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold mb-4">Amenidades</h2>
            
            <div className="flex flex-wrap gap-2">
              {AMENITIES_OPTIONS.map(amenity => (
                <button
                  key={amenity}
                  type="button"
                  onClick={() => toggleAmenity(amenity)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                    formData.amenities.includes(amenity)
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {formData.amenities.includes(amenity) && (
                    <CheckCircle className="w-3 h-3 inline mr-1" />
                  )}
                  {amenity}
                </button>
              ))}
            </div>
          </div>

          {/* Images Section */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold mb-4">
              <Image className="w-5 h-5 inline mr-2" />
              Imágenes de la Propiedad
            </h2>
            
            {/* Upload Area */}
            <div className="mb-4">
              <label 
                className={`flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                  uploadingImages 
                    ? 'border-indigo-300 bg-indigo-50' 
                    : 'border-gray-300 hover:border-indigo-500 hover:bg-gray-50'
                }`}
              >
                <input
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleImageUpload}
                  className="hidden"
                  disabled={uploadingImages}
                />
                {uploadingImages ? (
                  <>
                    <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mb-2" />
                    <p className="text-sm text-indigo-600">Subiendo imágenes...</p>
                  </>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">Click para subir imágenes</p>
                    <p className="text-xs text-gray-400 mt-1">JPG, PNG, WebP o GIF (máx 10MB cada una)</p>
                  </>
                )}
              </label>
            </div>
            
            {/* Error Message */}
            {imageError && (
              <p className="text-sm text-red-500 mb-4">{imageError}</p>
            )}
            
            {/* Drag & Drop Instructions */}
            {formData.images.length > 1 && (
              <p className="text-xs text-gray-500 mb-3 flex items-center gap-1">
                <GripVertical className="w-3 h-3" />
                Arrastra las imágenes para reordenar. La primera será la imagen principal.
              </p>
            )}
            
            {/* Images Grid with Drag & Drop */}
            {formData.images.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {formData.images.map((img, index) => (
                  <div 
                    key={img.filename || index} 
                    className={`relative group aspect-square cursor-move ${
                      draggedIndex === index ? 'opacity-50 scale-95' : ''
                    }`}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                  >
                    <img
                      src={`${API_URL}${img.url}`}
                      alt={`Imagen ${index + 1}`}
                      className="w-full h-full object-cover rounded-lg border-2 border-transparent hover:border-indigo-300 transition-all"
                    />
                    
                    {/* Overlay with actions */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all rounded-lg flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                      {/* Zoom button */}
                      <button
                        type="button"
                        onClick={() => setPreviewImage(img)}
                        className="p-2 bg-white/90 text-gray-700 rounded-full hover:bg-white transition-colors"
                        title="Ver imagen"
                      >
                        <ZoomIn className="w-4 h-4" />
                      </button>
                      
                      {/* Set as main button (only if not first) */}
                      {index !== 0 && (
                        <button
                          type="button"
                          onClick={() => setAsMainImage(index)}
                          className="p-2 bg-yellow-500 text-white rounded-full hover:bg-yellow-600 transition-colors"
                          title="Establecer como principal"
                        >
                          <Star className="w-4 h-4" />
                        </button>
                      )}
                      
                      {/* Delete button */}
                      <button
                        type="button"
                        onClick={() => removeImage(index, img.filename)}
                        className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    
                    {/* Drag handle indicator */}
                    <div className="absolute top-2 left-2 p-1 bg-white/80 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                      <GripVertical className="w-4 h-4 text-gray-500" />
                    </div>
                    
                    {/* Main image badge */}
                    {index === 0 && (
                      <span className="absolute bottom-2 left-2 px-2 py-1 bg-indigo-600 text-white text-xs rounded-full flex items-center gap-1">
                        <Star className="w-3 h-3" />
                        Principal
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {formData.images.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">
                No hay imágenes. Sube al menos una imagen de tu propiedad.
              </p>
            )}
          </div>

          {/* Image Preview Modal */}
          {previewImage && (
            <div 
              className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
              onClick={() => setPreviewImage(null)}
            >
              <div className="relative max-w-4xl max-h-[90vh] w-full">
                <button
                  type="button"
                  onClick={() => setPreviewImage(null)}
                  className="absolute -top-12 right-0 p-2 text-white hover:text-gray-300 transition-colors"
                >
                  <X className="w-8 h-8" />
                </button>
                <img
                  src={`${API_URL}${previewImage.url}`}
                  alt="Vista previa"
                  className="w-full h-full object-contain rounded-lg"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              Guardar
            </button>
            
            {isEditing && formData.status === 'draft' && (
              <button
                type="button"
                onClick={handlePublish}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Eye className="w-5 h-5" />
                Publicar
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
